// Der kleine Rechner im Betragsfeld (aus Finanzen, Auftrag 010 D1).
//
// „3000*1,19“ darf im Feld stehen, statt dass man es im Kopf ausrechnet.
// Bewusst ein eigener Parser statt `eval` oder `new Function`: Hier kann
// nichts anderes herauskommen als eine Zahl.
//
// Dezimaltrennzeichen: Komma und Punkt gelten beide. Punkte sind
// Tausenderpunkte, wenn ein Komma danebensteht („3.000,50“) oder wenn sie
// genau Dreiergruppen abteilen („3.000“, „1.234.567“). Sonst ist der Punkt das
// Dezimaltrennzeichen („1.19“), damit die Taschenrechner-Schreibweise geht.

type Zeichen = { art: 'zahl'; wert: number } | { art: 'zeichen'; wert: string };

const ZAHL = /^(?:\d+(?:\.\d*)?|\.\d+)/;
const SONDERZEICHEN = new Set(['+', '-', '*', '/', '(', ')']);

/** Zahlen mit Tausenderpunkten, aber ohne Komma: „3.000“, „1.234.567“. */
const TAUSENDER = /\d{1,3}(?:\.\d{3})+(?!\d)/g;

/** Leerraum weg, Komma und Punkt auf eine Schreibweise bringen. */
function normalisiere(eingabe: string): string {
  const ohneLeerraum = eingabe.replace(/\s+/g, '');
  // Mit Komma sind alle Punkte Tausenderpunkte. Ohne Komma nur die, die sauber
  // Dreiergruppen abteilen: „1.19“ bleibt ein Faktor, „3.000“ wird dreitausend.
  const ohneTausender = ohneLeerraum.includes(',')
    ? ohneLeerraum.replace(/\./g, '')
    : ohneLeerraum.replace(TAUSENDER, (zahl) => zahl.replace(/\./g, ''));
  return ohneTausender.replace(/,/g, '.');
}

function zerlege(text: string): Zeichen[] | null {
  const zeichen: Zeichen[] = [];
  let i = 0;
  while (i < text.length) {
    if (SONDERZEICHEN.has(text[i])) {
      zeichen.push({ art: 'zeichen', wert: text[i] });
      i += 1;
      continue;
    }
    const treffer = ZAHL.exec(text.slice(i));
    if (!treffer) return null;
    zeichen.push({ art: 'zahl', wert: Number(treffer[0]) });
    i += treffer[0].length;
  }
  return zeichen;
}

/**
 * Rechnet die Eingabe aus, `null`, wenn sie keine lesbare Rechnung ist.
 *
 * Punkt vor Strich, Klammern und Vorzeichen erlaubt. Division durch null ergibt
 * `null` statt `Infinity`: Ein Betragsfeld hat dafür keine sinnvolle Anzeige.
 */
export function auswerten(eingabe: string): number | null {
  const zerlegt = zerlege(normalisiere(eingabe));
  if (!zerlegt || zerlegt.length === 0) return null;

  const alle: Zeichen[] = zerlegt;
  let pos = 0;

  const zeichenWert = (): string | null => {
    const z = alle[pos];
    return z !== undefined && z.art === 'zeichen' ? z.wert : null;
  };

  function faktor(): number | null {
    const vorzeichen = zeichenWert();
    if (vorzeichen === '-' || vorzeichen === '+') {
      pos += 1;
      const wert = faktor();
      if (wert === null) return null;
      return vorzeichen === '-' ? -wert : wert;
    }
    if (zeichenWert() === '(') {
      pos += 1;
      const wert = summe();
      if (wert === null || zeichenWert() !== ')') return null;
      pos += 1;
      return wert;
    }
    const z = alle[pos];
    if (z === undefined || z.art !== 'zahl') return null;
    pos += 1;
    return z.wert;
  }

  function produkt(): number | null {
    let links = faktor();
    if (links === null) return null;
    for (;;) {
      const operator = zeichenWert();
      if (operator !== '*' && operator !== '/') return links;
      pos += 1;
      const rechts = faktor();
      if (rechts === null) return null;
      if (operator === '/' && rechts === 0) return null;
      links = operator === '*' ? links * rechts : links / rechts;
    }
  }

  function summe(): number | null {
    let links = produkt();
    if (links === null) return null;
    for (;;) {
      const operator = zeichenWert();
      if (operator !== '+' && operator !== '-') return links;
      pos += 1;
      const rechts = produkt();
      if (rechts === null) return null;
      links = operator === '+' ? links + rechts : links - rechts;
    }
  }

  const ergebnis = summe();
  // Nicht aufgebrauchte Zeichen heißen: Die Eingabe war nur teilweise lesbar.
  // Ein halbes Ergebnis wäre schlimmer als keines, es sieht richtig aus.
  if (ergebnis === null || pos !== alle.length) return null;
  return Number.isFinite(ergebnis) ? ergebnis : null;
}

/**
 * Steht da eine Rechnung oder nur eine Zahl? Ein führendes Minus ist ein
 * Vorzeichen, keine Rechnung.
 */
export function istAusdruck(eingabe: string): boolean {
  const text = normalisiere(eingabe);
  return /[+*/()]/.test(text) || /.-/.test(text);
}

/**
 * Fehlt der Eingabe nur das Ende? „3000*“, „(1000+500“, „-“: Mit einer Zahl und
 * den fehlenden Klammern dahinter wäre sie lesbar. „3000**“ oder „abc“ bleiben
 * es mit keiner Fortsetzung.
 */
function istUnfertig(eingabe: string): boolean {
  const zeichen = zerlege(normalisiere(eingabe));
  if (!zeichen || zeichen.length === 0) return false;
  let offen = 0;
  for (const z of zeichen) {
    if (z.art === 'zeichen' && z.wert === '(') offen += 1;
    if (z.art === 'zeichen' && z.wert === ')') offen -= 1;
    if (offen < 0) return false;
  }
  const letztes = zeichen[zeichen.length - 1];
  const fehltZahl = letztes.art === 'zeichen' && letztes.wert !== ')';
  if (!fehltZahl && offen === 0) return false;
  return auswerten(`${eingabe}${fehltZahl ? '1' : ''}${')'.repeat(offen)}`) !== null;
}

export type Betragspruefung =
  | { art: 'leer' }
  | { art: 'zahl'; wert: number; rechnung: boolean }
  | { art: 'unfertig' }
  | { art: 'ungueltig' };

/** Was im Feld steht: nichts, eine Zahl, eine angefangene Rechnung oder Unlesbares. */
export function pruefeBetrag(eingabe: string): Betragspruefung {
  if (!eingabe.trim()) return { art: 'leer' };
  const wert = auswerten(eingabe);
  if (wert !== null) return { art: 'zahl', wert, rechnung: istAusdruck(eingabe) };
  return istUnfertig(eingabe) ? { art: 'unfertig' } : { art: 'ungueltig' };
}

/**
 * Der Wert so, wie die APIs ihn als Decimal erwarten: Punkt, keine
 * Tausenderpunkte, auf `nachkommastellen` gerundet. Die Rundung ist kein
 * Schönheitsdetail: „0,1+0,2“ ergibt 0.30000000000000004, und das übernähme
 * Pydantic als Decimal mit siebzehn Stellen.
 */
export function zahlFuerApi(wert: number, nachkommastellen = 2): string {
  return String(Number(wert.toFixed(nachkommastellen)));
}

/**
 * Ein Decimal-Text so, wie er im Feld stehen soll: deutsches Komma, keine
 * Tausenderpunkte. `pruefeBetrag` liest ihn unverändert zurück.
 */
export function alsEingabe(dezimal: string): string {
  if (dezimal.trim() === '') return '';
  const wert = Number(dezimal);
  return Number.isNaN(wert) ? dezimal : String(wert).replace('.', ',');
}
